import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import i18n from '@/i18n';
import { isPasswordChangeRequiredError, isSessionRevokedError } from '@/lib/passwordChange';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Timeout de sessão PADRÃO: 2 horas de inatividade. É só o default — o valor
// efetivo vem de user.session_timeout_minutes (config por usuário, não mais
// hard-coded): 0 = nunca desloga; null/ausente = este padrão; >0 = minutos.
const DEFAULT_SESSION_TIMEOUT_MS = 2 * 60 * 60 * 1000;

// ms de inatividade até deslogar, a partir do usuário. null = nunca desloga.
const effectiveSessionTimeoutMs = (user) => {
  const m = user?.session_timeout_minutes;
  if (m === 0) return null;                  // nunca expira
  if (typeof m === 'number' && m > 0) return m * 60 * 1000;
  return DEFAULT_SESSION_TIMEOUT_MS;         // null/ausente = padrão do sistema
};

// Helper: limpa CPF removendo formatação
const cleanCPF = (cpf) => cpf ? cpf.replace(/\D/g, '') : '';
const normalizeLoginIdentifier = (identifier) => {
  const raw = identifier ? identifier.trim() : '';
  if (/^[\d.\-\s]+$/.test(raw)) {
    return cleanCPF(raw);
  }
  return raw.replace(/\s+/g, '').toUpperCase();
};

// ========================================================================
// DESENVOLVIMENTO: Modo de autenticação
// DEV_MODE_NO_AUTH=true bypassa login (apenas para desenvolvimento)
// ========================================================================
const DEV_MODE_NO_AUTH = false;
const DEV_USER = {
  id: 'dev-admin-user',
  username: 'admin',
  email: 'admin@octopus.dev',
  name: 'Desenvolvedor',
  role: 'admin',
  status: 'active',
  language: 'pt'
};
const DEV_TOKEN = 'dev-mode-token-not-for-production';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(DEV_MODE_NO_AUTH ? DEV_USER : null);
  const [token, setToken] = useState(() => {
    if (DEV_MODE_NO_AUTH) return DEV_TOKEN;
    return sessionStorage.getItem('octopus_token');
  });
  const [loading, setLoading] = useState(!DEV_MODE_NO_AUTH);
  const [permissions, setPermissions] = useState(DEV_MODE_NO_AUTH ? ['*:*'] : []);
  const [visibleEquipments, setVisibleEquipments] = useState([]);
  const initializedRef = useRef(false);
  const activityTimeoutRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  // 403 PASSWORD_CHANGE_REQUIRED em qualquer chamada: marca a flag no usuario e
  // o guard de rota (App.js) redireciona para /change-password.
  // 401 (sessao revogada por reset/troca de senha, expirada): limpa a sessao
  // local — sem chamar /auth/logout, que tambem responderia 401.
  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (isPasswordChangeRequiredError(error)) {
          setUser((prev) => (prev && !prev.must_change_password
            ? { ...prev, must_change_password: true }
            : prev));
        } else if (
          isSessionRevokedError(error)
          && sessionStorage.getItem('octopus_token')
          // 401 atrasado de um token antigo nao derruba a sessao nova (pos-troca/relogin).
          && String(error?.config?.headers?.Authorization || '') === `Bearer ${sessionStorage.getItem('octopus_token')}`
        ) {
          sessionStorage.removeItem('octopus_token');
          delete axios.defaults.headers.common['Authorization'];
          setToken(null);
          setUser(null);
          setPermissions([]);
          setVisibleEquipments([]);
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptorId);
  }, []);

  const logout = useCallback(async () => {
    // Try to log the logout on server
    try {
      if (token) {
        await axios.post(`${API}/auth/logout`);
      }
    } catch (error) {
      console.error('Error logging logout:', error);
    }

    sessionStorage.removeItem('octopus_token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    setPermissions([]);
    setVisibleEquipments([]);

    if (activityTimeoutRef.current) {
      clearTimeout(activityTimeoutRef.current);
    }
  }, [token]);

  // Reset inactivity timer
  const resetActivityTimer = useCallback(() => {
    lastActivityRef.current = Date.now();
    
    if (activityTimeoutRef.current) {
      clearTimeout(activityTimeoutRef.current);
    }
    
    const timeoutMs = effectiveSessionTimeoutMs(user);
    if (token && timeoutMs !== null) {
      activityTimeoutRef.current = setTimeout(() => {
        console.log('Session expired due to inactivity');
        logout();
      }, timeoutMs);
    }
    // timeoutMs === null => usuário configurado para nunca expirar: não arma o timer.
  }, [logout, token, user]);

  // Track user activity
  useEffect(() => {
    const handleActivity = () => {
      resetActivityTimer();
    };

    // Listen for user activity
    window.addEventListener('mousemove', handleActivity);
    window.addEventListener('keypress', handleActivity);
    window.addEventListener('click', handleActivity);
    window.addEventListener('scroll', handleActivity);
    window.addEventListener('touchstart', handleActivity);

    return () => {
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('keypress', handleActivity);
      window.removeEventListener('click', handleActivity);
      window.removeEventListener('scroll', handleActivity);
      window.removeEventListener('touchstart', handleActivity);
      
      if (activityTimeoutRef.current) {
        clearTimeout(activityTimeoutRef.current);
      }
    };
  }, [resetActivityTimer]);

  const loadAuthorizationContext = useCallback(async () => {
    try {
      const permissionsResponse = await axios.get(`${API}/rbac/my-permissions`);
      const nextPermissions = permissionsResponse.data?.permissions || [];
      setPermissions(nextPermissions);

      if (nextPermissions.includes('*:*') || nextPermissions.includes('operator_dashboard:read')) {
        const equipmentResponse = await axios.get(`${API}/equipments`);
        setVisibleEquipments(Array.isArray(equipmentResponse.data) ? equipmentResponse.data : []);
      } else {
        setVisibleEquipments([]);
      }
    } catch (error) {
      console.error('Failed to fetch authorization context:', error);
      setPermissions([]);
      setVisibleEquipments([]);
    }
  }, []);

  const fetchUser = useCallback(async (authToken) => {
    // DEV MODE: Skip actual API call
    if (DEV_MODE_NO_AUTH) {
      setUser(DEV_USER);
      setPermissions(['*:*']);
      setLoading(false);
      return;
    }
    
    try {
      axios.defaults.headers.common['Authorization'] = `Bearer ${authToken}`;
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      if (response.data.language) {
        i18n.changeLanguage(response.data.language);
      }
      // Com troca obrigatoria pendente as demais rotas respondem 403: so carrega
      // permissoes depois da troca.
      if (!response.data.must_change_password) {
        await loadAuthorizationContext();
      }
      resetActivityTimer();
    } catch (error) {
      console.error('Failed to fetch user:', error);
      sessionStorage.removeItem('octopus_token');
      delete axios.defaults.headers.common['Authorization'];
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [loadAuthorizationContext, resetActivityTimer]);

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    
    // DEV MODE: Set axios header with dev token
    if (DEV_MODE_NO_AUTH) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${DEV_TOKEN}`;
      setLoading(false);
      return;
    }
    
    const storedToken = sessionStorage.getItem('octopus_token');
    if (storedToken) {
      fetchUser(storedToken);
    } else {
      setLoading(false);
    }
  }, [fetchUser]);

  const login = async (username, password) => {
    const normalizedUsername = normalizeLoginIdentifier(username);
    const response = await axios.post(`${API}/auth/login`, { username: normalizedUsername, password });
    const { token: newToken, user: rawUser } = response.data;
    const userData = {
      ...rawUser,
      must_change_password: Boolean(response.data.must_change_password ?? rawUser?.must_change_password),
    };
    
    sessionStorage.setItem('octopus_token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    if (userData.language) {
      i18n.changeLanguage(userData.language);
    }
    if (!userData.must_change_password) {
      await loadAuthorizationContext();
    }
    
    resetActivityTimer();
    
    return userData;
  };

  // Troca da propria senha. O backend revoga as sessoes antigas e devolve um
  // token novo, que passa a ser o da sessao atual.
  const changePassword = async (currentPassword, newPassword) => {
    const response = await axios.post(`${API}/auth/change-password`, {
      current_password: currentPassword,
      new_password: newPassword,
    });
    const newToken = response.data?.token;
    if (newToken) {
      sessionStorage.setItem('octopus_token', newToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      setToken(newToken);
    }
    setUser((prev) => (prev ? { ...prev, must_change_password: false } : prev));
    await loadAuthorizationContext();
    return response.data;
  };

  const register = async (fullName, username, email, password, language, role = 'user') => {
    // Criação de conta é ato de admin (POST /auth/register exige o token já
    // anexado pelo login). Não troca a sessão pelo usuário recém-criado.
    // Auto-cadastro público segue em /auth/signup.
    const response = await axios.post(`${API}/auth/register`, {
      full_name: fullName,
      username,
      email,
      password,
      language,
      role
    });
    return response.data?.user ?? null;
  };

  const updateUserLanguage = (language) => {
    if (user) {
      setUser({ ...user, language });
      i18n.changeLanguage(language);
    }
  };

  const updateUserHomePage = (homePage) => {
    if (user) {
      setUser({ ...user, home_page: homePage });
    }
  };

  // Check if user is admin
  const hasPermission = useCallback((permission) => {
    if (!permission) return true;
    // Admin é superusuário: vê tudo, mesmo que a lista de permissões resolvida venha
    // vazia (o backend trata o papel admin via role, não expandindo '*:*' por permissão).
    if (user?.role === 'admin' || permissions.includes('*:*')) return true;
    const [resource] = permission.split(':');
    return permissions.includes(permission) || permissions.includes(`${resource}:*`);
  }, [permissions, user]);

  const isAdmin = user?.role === 'admin' || permissions.includes('*:*');
  const isOperatorDashboardOnly = hasPermission('operator_dashboard:read')
    && !hasPermission('ai_helper:read')
    && !hasPermission('equipment:update')
    && !isAdmin;

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      loading, 
      login, 
      register, 
      logout,
      changePassword,
      mustChangePassword: Boolean(user?.must_change_password),
      updateUserLanguage,
      updateUserHomePage,
      permissions,
      visibleEquipments,
      hasPermission,
      isAuthenticated: !!user,
      isAdmin,
      isOperatorDashboardOnly
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
